import {
  ALL_CONFIGS,
  AXIOS_TIMEOUT,
  BASIS_POINT,
  COEFF_PRICE_PUBLIC_IDO,
  DEFAULT_TOKEN_IDO_CURVE,
  DEFAULT_TOKEN_INIT_IDO_CURVE,
  EVM_DECIMAL,
  isEvmNetwork,
  MAX_INTEGER,
  SOL_DECIMAL,
  SPL_DECIMAL,
  TOTAL_SUPPLY_TOKEN,
} from '@/config';
import { fetchJSONDataFromUrl } from '@/program/logListeners/utils';
import { BN } from '@coral-xyz/anchor';
import { PublicKey, PublicKeyInitData } from '@solana/web3.js';
import axios, { AxiosRequestConfig } from 'axios';
import BigNumber from 'bignumber.js';
import {
  CoinInfo,
  HolderInfo,
  MsgInfo,
  RecordInfo,
  ReplyInfo,
  ReportInfo,
  TradeInfo,
  UserInfo,
} from './types';
import usePersistStore from '@/zustand-store/persist-config/usePersistStore';
import { HELIUS_RPC_DEFAULT } from '@/rpcConfig';
import base58 from 'bs58';
import { getEvmProvider } from '@/hooks/useConnectEvm';
import { successAlert } from '@/components/others/ToastGroup';
import { fetchRetry } from './api/axios';
import { ENDPOINTS } from './endpoints';
import { ethers } from 'ethers';

const currentNetwork = usePersistStore.getState().currentNetwork;

export const NATIVE_TOKEN_DECIMALS =
  currentNetwork === 'solana' ? SOL_DECIMAL : EVM_DECIMAL;
export const TOKEN_DECIMALS =
  currentNetwork === 'solana' ? SPL_DECIMAL : EVM_DECIMAL;

const headers: Record<string, string> = {
  'ngrok-skip-browser-warning': 'true',
};

export const config: AxiosRequestConfig = {
  timeout: AXIOS_TIMEOUT,
  headers,
};

export const test = async () => {
  const res = await fetch(`${ENDPOINTS.BACKEND}`);
  const data = await res.json();
  console.log(data);
};

export const getUserByWalletAddress = async ({
  baseUrl,
  wallet,
}: {
  baseUrl: string;
  wallet: string;
}): Promise<UserInfo> => {
  try {
    const response = await axios.get(
      `${baseUrl}/user/wallet/${wallet}`,
      config,
    );
    // console.log("response:", response.data);
    return response.data;
  } catch (err) {
    console.log('err getting user: ', err);
    throw new Error(err);
  }
};

export const updateStrategyConfigByCoin = async (
  id: string,
  data: any,
  baseUrl = ENDPOINTS.BACKEND,
): Promise<any> => {
  try {
    const response = await axios.post(
      `${baseUrl}/coin/token/${id}`,
      data,
      config,
    );
    return response.data;
  } catch (err) {
    console.log('err updateStrategyConfigByCoin', err);
    return;
  }
};

export const updateUser = async (id: string, data: UserInfo): Promise<any> => {
  try {
    const response = await axios.post(
      `${ENDPOINTS.BACKEND}/user/update/${id}`,
      data,
      config,
    );
    return response.data;
  } catch (err) {
    return { error: 'error setting up the request' };
  }
};

export const walletConnect = async ({
  data,
}: {
  data: UserInfo;
}): Promise<any> => {
  try {
    const response = await axios.post(
      `${ENDPOINTS.BACKEND}/user`,
      data,
      config,
    );
    return response.data;
  } catch (err) {
    return { error: 'error setting up the request' };
  }
};

export const confirmWallet = async ({
  data,
}: {
  data: UserInfo;
}): Promise<any> => {
  try {
    // TODO: this api is called 3 times when user login, need to fix
    const response = await axios.post(
      `${ENDPOINTS.BACKEND}/user/confirm`,
      data,
      config,
    );
    return response.data;
  } catch (err) {
    return { error: 'error setting up the request' };
  }
};

export const getCoinsInfo = async (
  baseUrl,
  params,
): Promise<{ coins: CoinInfo[]; isError?: boolean }> => {
  return baseUrl === ENDPOINTS.BACKEND_MEME
    ? getMemeCoinsInfo(params)
    : getAgentCoinsInfo(params);
};

export const getUpcoming = async (
  params,
): Promise<{ result: any; isError?: boolean }> => {
  return getUpcomingCoins(params);
};

export const getUpcomingCoins = async (
  params,
): Promise<{ result: any; isError?: boolean }> => {
  try {
    const res = await axios.get(`${ENDPOINTS.BACKEND}/upcoming`, {
      ...config,
      params,
    });
    return res.data;
  } catch (error) {
    console.log('getUpcomingCoins from BE failed', error);
    return {
      result: [],
      isError: true,
    };
  }
};

export const getAgentCoinsInfo = async (
  params,
): Promise<{ coins: CoinInfo[]; isError?: boolean }> => {
  try {
    const res = await axios.get(`${ENDPOINTS.BACKEND}/coin`, {
      ...config,
      params,
    });
    return res.data;
  } catch (error) {
    console.log('Get List agent token from BE failed', error);
    return {
      coins: [],
      isError: true,
    };
  }
};

export const getMemeCoinsInfo = async (
  params,
): Promise<{ coins: CoinInfo[]; isError?: boolean }> => {
  try {
    const res = await axios.get(`${ENDPOINTS.BACKEND_MEME}/coin`, {
      ...config,
      params,
    });
    return res.data;
  } catch (error) {
    console.log('Get List meme token from BE failed', error);
    return {
      coins: [],
      isError: true,
    };
  }
};

export const getMemeCoinsDetailByAddress = async (
  tokenAddress,
): Promise<{ tokenAddress: string }> => {
  try {
    const res = await axios.get(
      `${ENDPOINTS.BACKEND_MEME}/coin/${tokenAddress}`,
      {
        ...config,
      },
    );
    return res.data;
  } catch (error) {
    console.log('Get List meme token from BE failed', error);
    return;
  }
};

export const getAgentsData = async (params): Promise<CoinInfo[]> => {
  const res = await axios.get(`${ENDPOINTS.DISTILL}/user/search`, {
    ...config,
    params: {
      filter: JSON.stringify({
        username: '',
        status: 1,
        role: 4,
        publish: 1,
        ...(params.filter || {}),
      }),
      limit: 50,
      offset: 0,
    },
  });
  return res.data;
};

const MOCK_AGENTS_DATA = {
  data: {
    isMock: true,
    items: [
      {
        id: 2053,
        avatar: null,
        username: 'Agent EVM Beta',
        publicAddress: '0x77a4a450eebb2074e96cdf2b4c381f3c9ae15eae',
        owner: 1931,
        publish: 0,
        typeAgent: 1,
        status: 1,
        createdAt: '2025-03-24T10:40:18.329Z',
        botWallet: {
          id: 88,
          userId: 2053,
          solAddr: 'EERV9xE9cBxiqNoDy9atv6JN2NaLt74iqwaVSBnzYqyF',
          oraiAddr: 'orai18jt7qdlhkh5gk7nwcyn9xl320cg89amn94r295',
          oraiPublicKey: `{"3","79","83","144","29","255","225","15","28","91","121","185","34","91","5","221","169","145","80","36","182","122","163","164","98","167","117","102","144","144","203","87","12"}`,
          evmAddr: '0x77a4a450eebb2074e96cdf2b4c381f3c9ae15eae',
          evmPublicKey:
            '0x4f53901dffe10f1c5b79b9225b05dda9915024b67aa3a462a775669090cb570cedc7f74d8e0b3e1dd67bea8a2b8b4f5f8c9149971911a6bc16923822ea582c91',
          tokenize: null,
          createdAt: '2025-03-24T11:43:42.444Z',
        },
        ownedGroups: [
          {
            id: 792,
            name: 'EvmAgent.Clan',
            image: null,
            label: '@EvmAgent',
            userAId: null,
            userBId: null,
            createBy: 1931,
            status: 1,
            live: 1,
            typeGroup: 'PUBLIC_GROUP',
            config: null,
            eventId: null,
            clanOfAgentId: '1932',
            createdAt: '2025-03-14T04:40:10.209Z',
          },
          {
            id: 851,
            name: 'AgentEVMBeta.Clan',
            image: null,
            label: '@AgentEVMBeta',
            userAId: null,
            userBId: null,
            createBy: 1931,
            status: 1,
            live: 1,
            typeGroup: 'PUBLIC_GROUP',
            config: null,
            eventId: null,
            clanOfAgentId: '2053',
            createdAt: '2025-03-24T11:43:42.487Z',
          },
          {
            id: 816,
            name: 'AgentEvm001.Clan',
            image: null,
            label: '@AgentEvm001',
            userAId: null,
            userBId: null,
            createBy: 1931,
            status: 1,
            live: 1,
            typeGroup: 'PUBLIC_GROUP',
            config: null,
            eventId: null,
            clanOfAgentId: '1976',
            createdAt: '2025-03-19T06:59:01.652Z',
          },
        ],
      },
    ],
    total: 1,
  },
};

export const getAgentsDataByUser = async (params): Promise<any[]> => {
  const isDevNet = import.meta.env.VITE_APP_SOLANA_ENV === 'devnet';

  const testnetEnv = isDevNet && isEvmNetwork;
  const ownerAddress = testnetEnv
    ? '0xE27d3094C4231150d421c3600DEd0d130cf74216'
    : params.user;

  const res = testnetEnv
    ? MOCK_AGENTS_DATA
    : await axios.get(`${ENDPOINTS.DISTILL}/bot/public/list`, {
        ...config,
        params: {
          filter: JSON.stringify({
            ownerAddress,
          }),
          limit: 100,
          offset: 0,
        },
      });

  return res.data;
};

export const getCoinsInfoBy = async (
  baseUrl: string,
  id: string,
): Promise<CoinInfo[]> => {
  const res = await axios.get<CoinInfo[]>(`${baseUrl}/coin/user/${id}`, config);
  return res.data.map((info) => ({
    ...info,
    tokenReserves: toBN(info.tokenReserves?.toString()) as any,
    lamportReserves: toBN(info.lamportReserves?.toString()) as any,
  }));
};

export const genTokenKeypair = async (baseUrl: string): Promise<string> => {
  const res = await axios.get<{
    privateKey: string;
  }>(`${baseUrl}/keypair`, config);
  return res.data.privateKey;
};

export const getCoinInfo = async (
  baseUrl: string,
  data: string,
): Promise<CoinInfo> => {
  try {
    const response = await axios.get(`${baseUrl}/coin/${data}`, config);

    let metadata = response.data?.metadata;
    if (!metadata && response?.data?.url) {
      metadata = await fetchJSONDataFromUrl(response.data.url);
    }

    return {
      ...response.data,
      tokenReserves: new BN(response.data.tokenReserves),
      lamportReserves: new BN(response.data.lamportReserves),
      metadata,
      description: metadata?.description,
    };
  } catch (err) {
    console.log('err get coin info: ', err);
    // throw new Error(err);
  }
};

export const getMessageByCoin = async (
  baseUrl: string,
  data: string,
): Promise<MsgInfo[]> => {
  try {
    const response = await axios.get(
      `${baseUrl}/feedback/coin/${data}`,
      config,
    );
    return response.data;
  } catch (err) {
    console.log('err get message by coin: ', err);
  }
};

export const getCoinTrade = async (
  baseUrl: string,
  data: string,
): Promise<TradeInfo> => {
  try {
    const response = await axios.get(`${baseUrl}/cointrade/${data}`, config);
    return {
      ...response.data,
      record: response.data.record.map((r: RecordInfo) => ({
        ...r,
        lamportAmount: toBN(r.lamportAmount?.toString()),
        tokenAmount: toBN(r.tokenAmount?.toString()),
      })),
    };
  } catch (err) {
    console.log('err get coin trade: ', err);
  }
};

export const getKoth = async (baseUrl: string): Promise<CoinInfo> => {
  try {
    const response = await axios.get(`${baseUrl}/coin/king/koth`, config);
    return {
      ...response.data,
    };
  } catch (err) {
    console.log('err get coin king: ', err);
    // throw new Error(err);
  }
};

export const postReply = async (baseUrl: string, data: ReplyInfo) => {
  try {
    const response = await axios.post(`${baseUrl}/feedback`, data, config);
    return response.data;
  } catch (err) {
    return { error: 'error setting up the request' };
  }
};

export const reportToken = async (data: ReportInfo) => {
  try {
    const response = await axios.post(
      `${ENDPOINTS.BACKEND}/report`,
      data,
      config,
    );
    return response.data;
  } catch (err) {
    console.log('error reportToken', err);
    return;
  }
};

export const getReportedByUserAndCoin = async (
  wallet: string,
  coinId: string,
) => {
  try {
    const response = await axios.get(
      `${ENDPOINTS.BACKEND}/report/coin/${coinId}/user/${wallet}`,
      config,
    );
    return response.data;
  } catch (err) {
    console.log('error get report coin by user request', err);
    return;
  }
};

// ================== Get Holders ===========================
export const findHolders = async (
  mint: string,
  rpcUrl: string = HELIUS_RPC_DEFAULT,
) => {
  let allOwners: HolderInfo[] = [];

  const HELIUS_RPC =
    rpcUrl ||
    import.meta.env.VITE_SOLANA_RPC ||
    'https://devnet.helius-rpc.com/?api-key=44b7171f-7de7-4e68-9d08-eff1ef7529bd';

  // query top 20 holders
  try {
    const response = await fetch(HELIUS_RPC, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'getTokenAccounts',
        id: 'helius-test',
        params: {
          page: 1,
          limit: 20,
          displayOptions: {},
          //mint address for the token we are interested in
          mint: mint,
        },
      }),
    });
    const data = await response.json();
    // error when querying -> return empty holder
    if (!data?.result?.token_accounts) return [];

    // Adding unique owners to a list of token owners.
    data.result.token_accounts.forEach((account) => {
      allOwners.push({
        slice: account.owner.slice(0, 3) + `...` + account.owner.slice(-4),
        owner: account.owner,
        amount: account.amount,
      });
    });

    return allOwners;
  } catch (error) {
    console.log('Error finding token holders: ', error);
    return [];
  }
};

export const findHoldersFromBE = async (
  baseUrl: string,
  mint: string,
  limit: number = 20,
) => {
  try {
    const response = await axios.get(
      `${baseUrl}/coin/token/${mint}/holder?limit=${limit}`,
      config,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching findHoldersFromBE price:', error);
  }
};

export const findMarketCapFromBE = async (baseUrl: string, mint: string) => {
  try {
    const response = await axios.get(
      `${baseUrl}/coin/token/${mint}/market`,
      config,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching findMarketCapFromBE price:', error);
    return;
  }
};

export const getSolPriceInUSD = async () => {
  try {
    // Fetch the price data from CoinGecko
    const response = await axios.get(
      // "https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd"
      'https://price.market.orai.io/simple/price?ids=solana%2Cmax-2%2Coraichain-token&vs_currencies=usd',
    );
    const solPriceInUSD = response.data?.solana?.usd;
    const maxPriceUsd = response.data?.['max-2']?.usd;
    const oraiPriceUsd = response.data?.['oraichain-token']?.usd;
    return { solPriceInUSD, maxPriceUsd, oraiPriceUsd };
  } catch (error) {
    console.error('Error fetching price:', error);
    throw error;
  }
};

export const getTokensPriceByIds = async (
  tokens: string[],
  cachedPrices = {},
): Promise<any> => {
  if (tokens.length === 0) return cachedPrices;

  const BASE_URL =
    'https://api.geckoterminal.com/api/v2/networks/solana/tokens/multi/';
  const TOKEN_LIST = tokens.map(encodeURIComponent).join(',');
  const fullUrl = BASE_URL + TOKEN_LIST;
  const prices = { ...cachedPrices };
  try {
    const resp = await fetchRetry(fullUrl);
    const rawData = await resp.json();
    // update cached
    for (const key of tokens) {
      const tokenInfo = rawData.data.find((item) => item.attributes.address);
      prices[key] = tokenInfo?.attributes?.price_usd;
    }
  } catch {
    // remain old cache
    console.log('error getTokensPriceByIds: ', prices);
  }
  return prices;
};

// ===========================Functions=====================================
const JWT = import.meta.env.VITE_PINATA_PRIVATE_KEY;

export const pinFileToIPFS = async (blob: File) => {
  try {
    const data = new FormData();
    data.append('file', blob);
    const res = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${JWT}`,
      },
      body: data,
    });
    const resData = await res.json();
    return resData;
  } catch (error) {
    console.log(error);
  }
};
export const uploadImagePinata = async (url: string) => {
  const res = await fetch(url);
  console.log(res.blob);
  const blob = await res.blob();

  const imageFile = new File([blob], 'image.png', { type: 'image/png' });
  console.log(imageFile);
  const resData = await pinFileToIPFS(imageFile);
  console.log(resData, 'RESDATA>>>>');
  if (resData) {
    return `https://gateway.pinata.cloud/ipfs/${resData.IpfsHash}`;
  } else {
    return false;
  }
};

/**
 * Converts a number to a BigNumber (BN) with the specified number of decimals.
 *
 * @param value - The number to convert.
 * @param decimals - The number of decimal places.
 * @returns A BigNumber (BN) representation of the input number with the specified decimals.
 */
export const toBig = (value: number, decimals: number): BN => {
  if (!value) return new BN(0);
  return new BN(value).mul(new BN(10 ** decimals));
};

/**
 * Converts a BN value to a number with the specified decimals.
 * @param value The BN value to convert.
 * @param decimals The number of decimals to consider.
 * @returns The converted number.
 */
export const fromBig = (value: BN | bigint, decimals: number = 6): number => {
  if (!value) return 0;
  // if (!(value instanceof BN)) {
  //   value = new BN(value);
  // }

  // console.log('value', value, value.toNumber());
  // const divmodResult = value.divmod(new BN(10 ** decimals));
  // const result =
  //   divmodResult.div.toString() +
  //   '.' +
  //   divmodResult.mod.toString().padStart(decimals, '0');
  // return parseFloat(result);

  const divResult = toBN(value.toString())
    .div(10 ** decimals)
    .toNumber();
  return divResult;
};

export const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const retrieveEnvVariable = (variableName: string) => {
  const variable = process.env[variableName] || '';
  if (!variable) {
    console.log(`${variableName} is not set`);
    // process.exit(1);
  }
  return variable;
};

export const toBN = (val: BigNumber.Value) => {
  return new BigNumber(val);
};

export const toPublicKey = (val: PublicKeyInitData) => {
  try {
    return new PublicKey(val);
  } catch (error) {
    console.log('error to PublicKey', error);
    return null;
  }
};

export const calculateMarketCap = (
  tokenReserves: BN,
  decimals: number,
  tokenPrice: number,
): number => {
  return TOTAL_SUPPLY_TOKEN * tokenPrice;
  // if (!(tokenReserves instanceof BN)) {
  //   tokenReserves = new BN(tokenReserves);
  // }
  // const tokenReservesNum = fromBig(tokenReserves, decimals);
  // return tokenReservesNum * tokenPrice;
};

export const calcIdoPrice = (curveInfo, onParty, onPublic) => {
  if (!curveInfo.partyAmount) {
    return 0;
  }

  const deltaDecimal = NATIVE_TOKEN_DECIMALS - TOKEN_DECIMALS;

  const initPrice =
    Number(curveInfo.curveLimit?.toString()) /
    Number(curveInfo.partyAmount?.toString()) /
    10 ** deltaDecimal;
  // div for delta decimal

  if (onParty || !onPublic) {
    return initPrice;
  }
  return initPrice * COEFF_PRICE_PUBLIC_IDO; // on public round must to mul 1.2, this coeff defined on contract
};

export const simulateBuyIdo = (price, solAmt) => {
  if (!price) {
    return '0';
  }

  return toBN(solAmt).multipliedBy(0.99).div(price).toString();
};

export const simulateSellIdo = (price, tokenAmt) => {
  if (!price) {
    return '0';
  }

  return toBN(tokenAmt).multipliedBy(0.99).multipliedBy(price).toString();
};

export const calcIdoCoinPrice = (coinItem, curveInfo?: any) => {
  const partyStart = Math.ceil(
    new Date(coinItem?.partyTradingTime || Date.now()).getTime() /
      ALL_CONFIGS.TIMER.MILLISECOND,
  );
  const publicStart = Math.floor(
    new Date(coinItem?.publicTradingTime || Date.now()).getTime() /
      ALL_CONFIGS.TIMER.MILLISECOND,
  );

  const isPartyStart =
    partyStart && partyStart * ALL_CONFIGS.TIMER.MILLISECONDS <= Date.now();
  const isPublicStart =
    publicStart && publicStart * ALL_CONFIGS.TIMER.MILLISECONDS <= Date.now();
  const isOnParty = !isPublicStart && isPartyStart;

  const idoPrice = calcIdoPrice(
    {
      partyAmount:
        curveInfo?.partyAmount?.toString() ||
        DEFAULT_TOKEN_IDO_CURVE * 10 ** 12,
      curveLimit:
        curveInfo?.['curveLimit']?.toString() ||
        coinItem?.bondingCurveLimit ||
        0,
    },
    isOnParty,
    isPublicStart,
  );

  return idoPrice;
};

export const calcBondingCurveOfIDO = (
  tokenReserve,
  tokenInit = DEFAULT_TOKEN_INIT_IDO_CURVE * 10 ** 12,
  tokenOnCurve = DEFAULT_TOKEN_IDO_CURVE * 10 ** 12,
) => {
  return toBN(
    // sold token amount
    toBN(tokenInit).minus(
      tokenReserve?.toString() || DEFAULT_TOKEN_INIT_IDO_CURVE,
    ),
  )
    .multipliedBy(100)
    .div(tokenOnCurve)
    .toNumber();
};

export const simulateOutIdo = (
  coin,
  maxAmount,
  price,
  fee = 0.01,
  decimal = SOL_DECIMAL,
) => {
  if (!coin) return 0;
  return toBN(maxAmount)
    .multipliedBy(price)
    .div(1 - fee)
    .multipliedBy(10 ** decimal)
    .toNumber();
};

export const simulateOutEvmIdo = (
  coin,
  maxAmount,
  price,
  fee = 0.01,
  decimal = EVM_DECIMAL,
) => {
  if (!coin) return 0;
  return toBN(maxAmount)
    .multipliedBy(price)
    .div(1 - fee)
    .multipliedBy(10 ** decimal)
    .toNumber();
};

export const simulateOutTokenIdo = (
  coin,
  maxAmount,
  price,
  fee = 0.01,
  decimal = SOL_DECIMAL,
  isEvm = false,
) => {
  if (isEvm) {
    return simulateOutEvmIdo(coin, maxAmount, price, fee, decimal);
  }
  return simulateOutIdo(coin, maxAmount, price, fee, decimal);
};

export const simulateEvmSwap = (
  ethReserve: number,
  tokenReserve: number,
  amountIn: number,
  direction: number,
  swapFeeBps: number,
): number => {
  try {
    // Validate inputs
    if (ethReserve <= 0 || tokenReserve <= 0 || amountIn <= 0) {
      throw new Error('Reserves and amountIn must be greater than 0');
    }
    if (swapFeeBps < 0 || swapFeeBps > BASIS_POINT) {
      throw new Error('Swap fee must be between 0 and 10000 basis points');
    }

    // Calculate amount out based on direction
    if (direction === 0) {
      // Buy: ETH in, Token out (fee on input ETH)
      const amountInWithFee = amountIn * (BASIS_POINT - swapFeeBps);
      // const amountInWithFee = amountIn * BASIS_POINT;
      const numerator = amountInWithFee * tokenReserve;
      const denominator = ethReserve * BASIS_POINT + amountInWithFee;
      return numerator / denominator;
    } else {
      const numerator = amountIn * ethReserve;
      const denominator = tokenReserve + amountIn;
      const amountOutBeforeFee = numerator / denominator;

      // return amountOutBeforeFee;
      const fee = (amountOutBeforeFee * swapFeeBps) / BASIS_POINT;
      return amountOutBeforeFee - fee;
    }
  } catch (error) {
    console.error('Error calculating EVM swap:', error);
    return 0;
  }
};

/**
 * Sign a message based on currrent network
 * @param textSign
 * @param wallet
 * @returns
 */
export const signPersonalMessage = async ({
  textSign,
  wallet,
  address,
}: {
  textSign: string;
  wallet: any;
  address: string;
}): Promise<string> => {
  try {
    if (!address) throw new Error('Please connect wallet to sign message');
    let signature = '';
    if (!isEvmNetwork) {
      const msg = new TextEncoder().encode(`${textSign}`);
      const sig = await wallet.signMessage?.(msg);
      signature = base58.encode(sig as Uint8Array);
    } else {
      const msgHex = `0x${Buffer.from(textSign, 'utf8').toString('hex')}`;
      const evmProdiver = getEvmProvider();
      signature = await evmProdiver.request({
        method: 'personal_sign',
        params: [msgHex, address],
      });
    }
    return signature;
  } catch (error) {
    throw new Error(error);
  }
};

export const calcTokenRemainingOnCurve = (curveInfo, tokenReserve) => {
  const tokenAmountToMigrate = toBN(curveInfo.initLamport?.toString() || 0)
    .multipliedBy(curveInfo.initToken?.toString() || 0)
    .div(curveInfo['curveLimit']?.toString() || MAX_INTEGER);
  const tokenRemaining = toBN(tokenReserve)
    .minus(tokenAmountToMigrate)
    .toNumber();

  return tokenRemaining;
  // curveInfo['reserveToken']?.toString() ||
  //     (isEvmNetwork
  //       ? coin.tokenReserve?.toString()
  //       : coin.tokenReserves?.toString()) ||
  //     0,
};

export const handleLogin = async ({
  updatedUser,
  setUser,
  setLogin,
  confirmWalletWithSigature,
}: {
  updatedUser: UserInfo;
  setUser;
  setLogin;
  confirmWalletWithSigature;
}) => {
  try {
    const connection = await walletConnect({ data: updatedUser });
    if (!connection) return;
    if (connection.nonce === undefined) {
      const newUser = {
        name: connection.name,
        wallet: connection.wallet,
        _id: connection._id,
        avatar: connection.avatar,
      };
      setUser(newUser as UserInfo);
      setLogin(true);
      return;
    }
    const confirm = await confirmWalletWithSigature(connection);
    if (confirm) {
      setUser(confirm);
      setLogin(true);
    }
    successAlert('Message signed.');
  } catch (error) {
    console.log('error', error);
    throw new Error(error);
  }
};

export function generateEvmAddressFromText(text: string): string {
  // Hash the text using keccak256
  const hash = ethers.keccak256(ethers.toUtf8Bytes(text));

  // Take the last 20 bytes (40 hex characters)
  const address = '0x' + hash.slice(-40);

  // Return checksummed address (mixed case)
  const addressString = ethers.getAddress(address);

  console.log('addressString', addressString);

  return addressString;
}

export * from './api/axios';
export * from './calculations';
export * from './endpoints';
export * from './string';
